import type { ReactNode } from "react";

import WaveText from "@/components/ui/wave-text";
import { cn } from "@/lib/cn";

/** Desktop landing sections: one viewport tall with scroll-snap alignment. */
export const viewportSectionClasses =
  "lg:min-h-svh lg:snap-start lg:flex lg:flex-col lg:justify-center";

// Section titles mark their lime keyword with `*asterisks*` in the locale
// file — "A loop that *compounds*" renders "compounds" in the accent color,
// with the wave cascade flowing through the whole line in reading order.
function AccentedWaveTitle({ text }: { text: string }) {
  const parts = text.split(/\*([^*]+)\*/);
  let charsSoFar = 0;
  return (
    <span className="-mx-[0.08em] block overflow-visible px-[0.08em] pb-[0.08em]">
      {parts.map((part, index) => {
        const segment = part.trim();
        if (!segment) return null;
        const delay = charsSoFar * 0.03;
        charsSoFar += segment.length;
        const wave = <WaveText text={segment} delay={delay} yOffset={8} />;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional and static
          <span key={index}>
            {index > 0 ? " " : null}
            {index % 2 === 1 ? <span className="text-primary">{wave}</span> : wave}
          </span>
        );
      })}
    </span>
  );
}

type SectionProps = {
  children: ReactNode;
  className?: string;
  id?: string;
  contained?: boolean;
  /** On lg+ viewports, fill one screen and participate in landing scroll-snap. */
  viewport?: boolean;
};

export function Section({
  children,
  className,
  id,
  contained = true,
  viewport = false,
}: SectionProps) {
  const sectionClasses = cn(
    "relative w-full py-12 lg:py-16",
    viewport && viewportSectionClasses,
    className,
  );

  if (!contained) {
    return (
      <section id={id} className={sectionClasses}>
        {children}
      </section>
    );
  }
  return (
    <section id={id} className={sectionClasses}>
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6">{children}</div>
    </section>
  );
}

type SectionHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  align?: "left" | "center";
  className?: string;
  /** Use h1 for standalone route pages (SEO / a11y). */
  pageTitle?: boolean;
};

export function SectionHeader({
  title,
  subtitle,
  align = "left",
  className,
  pageTitle = false,
}: SectionHeaderProps) {
  const TitleTag = pageTitle ? "h1" : "h2";
  return (
    <header
      className={cn(
        "flex flex-col gap-4",
        align === "center" ? "mx-auto max-w-3xl items-center text-center" : "max-w-2xl",
        className,
      )}
    >
      <TitleTag
        className={cn(
          "text-balance font-sans font-semibold tracking-tight text-foreground",
          pageTitle ? "text-hero" : "text-display",
        )}
      >
        {/* String titles get the wave reveal as they scroll in (vikod3 style);
            custom nodes render as-is. */}
        {typeof title === "string" ? <AccentedWaveTitle text={title} /> : title}
      </TitleTag>
      {subtitle ? (
        <p className="text-pretty text-lg leading-relaxed text-muted-foreground md:text-xl">
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}
